import { useState } from "react";

import { Stack, Typography } from "@mui/material";
import { isSameDay } from "date-fns";

import { AppointmentResponse } from "@/api";
import { AppointmentEditDialog } from "@/common/components/appointments/appointment-edit/AppointmentEditDialog";
import { WeekCalendar } from "@/common/components/calendar/WeekCalendar";
import { WeekDayHeaderTitle } from "@/common/components/calendar/WeekDayHeaderTitle";
import { CounselorSelect } from "@/common/components/counselor-select/CounselorSelect";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import {
  formatAttendanceLabel,
  isAbsentAttendanceAppointment,
  mapAbsenceBackgroundEvent,
  mapAppointmentEvent,
} from "@/common/hooks/appointmentCalendar.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { useTranslation } from "@/translations";

import { AppointmentWeekWidget } from "./components/appointment-widget/AppointmentWeekWidget";
import { useAppointmentsWeek } from "./hooks/useAppointmentsWeek";

interface Props {
  selectedCounselorId: string | null;
  onCounselorChange: (counselorId: string | null) => void;
  onDateChange?: (start: Date, end: Date) => void;
  onAttendanceHeaderClick: () => void;
}

export function AppointmentsWeek({
  selectedCounselorId,
  onCounselorChange,
  onDateChange,
  onAttendanceHeaderClick,
}: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { purposeLabelOf } = useAppointmentLabels();
  const { calendarAppointments, attendanceAppointments, isLoading, onDateRangeChange } =
    useAppointmentsWeek(selectedCounselorId);
  const [editingAppointment, setEditingAppointment] = useState<AppointmentResponse | null>(null);

  // Without a selected counselor the week mixes everyone's appointments, so one person's absence
  // would mask the day for all of them.
  const absenceEvents = selectedCounselorId
    ? attendanceAppointments.filter(isAbsentAttendanceAppointment).map(mapAbsenceBackgroundEvent)
    : [];

  const handleDateChange = (start: Date, end: Date) => {
    onDateRangeChange(start, end);
    onDateChange?.(start, end);
  };

  return (
    <>
      <WeekCalendar<AppointmentResponse>
        events={calendarAppointments}
        backgroundEvents={absenceEvents}
        noEventsMessage={t("calendar.noAppointments")}
        renderEvent={(appointment) => (
          <AppointmentWeekWidget appointment={appointment} onOpen={() => setEditingAppointment(appointment)} />
        )}
        onDateChange={handleDateChange}
        isLoading={isLoading}
        mapEvent={mapAppointmentEvent}
        customHeaderFilter={
          <CounselorSelect selectedCounselor={selectedCounselorId} onSelectCounselor={onCounselorChange} />
        }
        onEventClick={(appointment: AppointmentResponse) => setEditingAppointment(appointment)}
        dayHeaderContent={(arg) => {
          const entries = attendanceAppointments.filter((a) => isSameDay(a.startTimeUtc, arg.date));
          return (
            <Stack alignItems="flex-start">
              <WeekDayHeaderTitle arg={arg} />
              {entries.map((a) => (
                <Typography
                  key={a.appointmentId}
                  variant="subtitle2"
                  color="text.secondary"
                  noWrap
                  sx={{ cursor: "pointer" }}
                  onClick={() => onAttendanceHeaderClick()}
                >
                  {formatAttendanceLabel(a, purposeLabelOf(a.purposeKey), t, language)}
                </Typography>
              ))}
            </Stack>
          );
        }}
      />
      {editingAppointment && (
        <AppointmentEditDialog appointment={editingAppointment} onClose={() => setEditingAppointment(null)} />
      )}
    </>
  );
}
